/**
 * Cross-browser helpers for the handful of DOM APIs whose behaviour differs
 * enough between Chrome, Edge, Firefox and Safari to break features outright.
 *
 * Each helper exists because the naive inline version failed on at least one
 * supported browser — the reasons are documented per function so nobody
 * "simplifies" them back into the broken form.
 */

/**
 * Trigger a browser download for an already-materialised Blob.
 *
 * Two browser differences the obvious four-line version gets wrong:
 *
 * 1. Firefox will not action a synthetic `.click()` on an `<a download>` that
 *    is not attached to the document. Chrome will. So the anchor has to be
 *    appended, clicked, and removed.
 *
 * 2. Revoking the object URL on the next line races the download. The click
 *    only *schedules* the fetch of the blob URL; Safari in particular has
 *    often not started reading it yet, and revoking first cancels the
 *    download silently — no error, no file. Revoking on a timeout lets the
 *    fetch start. (The URL is still revoked, so this does not leak.)
 */
export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    try {
        triggerAnchorDownload(url, filename);
    } finally {
        // Long enough for every supported browser to have begun the fetch.
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }
}

/**
 * Trigger a download from a URL the server already exposes (no Blob involved).
 * Same attached-anchor requirement as `downloadBlob`; nothing to revoke.
 */
export function downloadFromUrl(url: string, filename: string): void {
    triggerAnchorDownload(url, filename);
}

function triggerAnchorDownload(url: string, filename: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    // Hidden but still in the document tree — Firefox requires the latter.
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/**
 * Whether the browser can render this file well enough to print it.
 *
 * Only PDFs and raster images have a built-in renderer. Word/Excel/PowerPoint
 * have none — an iframe pointed at a .docx blob shows nothing and the print
 * dialog comes up blank — so callers fall back to downloading those instead.
 */
export function canPrintInBrowser(mimeType: string | null | undefined): boolean {
    const type = (mimeType || '').toLowerCase();
    return type.includes('pdf') || type.startsWith('image/');
}

/**
 * Open the browser's print dialog for an already-materialised Blob.
 *
 * Uses a hidden iframe rather than `window.open`: the blob only exists after an
 * async fetch, by which point the click gesture has ended and popup blockers
 * refuse the window — and driving print() on another window cross-document is
 * unreliable anyway. An iframe is same-origin and needs no gesture at print
 * time.
 *
 * Browser differences the naive version gets wrong:
 *
 * 1. The iframe must be attached, visible, AND large enough to lay out a page.
 *    `display:none` stops the PDF viewer loading at all, and a 1x1 box makes it
 *    load but render nothing — print() then produces blank sheets. It is sized
 *    to a full A4 at 96dpi and parked off-screen instead.
 *
 * 2. The print has to wait for the viewer to paint. `onload` fires when the
 *    document is fetched, not when the PDF is rendered; printing on that tick
 *    is the other way to get blank pages. There is no "rendered" event for the
 *    built-in viewer, so a short settle delay is the only option.
 *
 * 2b. Safari needs `contentWindow.focus()` before `print()` or the dialog never
 *    surfaces — the same reason the asset QR-label print does it.
 *
 * 3. Images are wrapped in a minimal HTML document instead of being loaded
 *    directly. A bare image URL prints at its intrinsic pixel size, which
 *    overflows the sheet for anything off a phone camera; the wrapper scales it
 *    to the printable area.
 *
 * 4. The object URL is revoked on a timeout, not immediately — revoking while
 *    the print preview still holds the document blanks the preview. Same race
 *    as `downloadBlob`.
 *
 * Returns false if the iframe could not be prepared, so callers can fall back
 * to a download rather than failing silently.
 */
export function printBlob(blob: Blob, mimeType: string | null | undefined): boolean {
    const url = URL.createObjectURL(blob);
    const isImage = (mimeType || '').toLowerCase().startsWith('image/');

    const frame = document.createElement('iframe');
    // Off-screen but fully laid out — see note 1. A4 at 96dpi; the viewer needs
    // a real viewport or it renders nothing and the print comes out blank.
    frame.setAttribute('aria-hidden', 'true');
    frame.style.position = 'fixed';
    frame.style.left = '-10000px';
    frame.style.top = '0';
    frame.style.width = '794px';
    frame.style.height = '1123px';
    frame.style.border = '0';
    // Deliberately NOT opacity:0 / visibility:hidden — both can suppress
    // painting, which is the same failure as sizing it 1x1. Being parked
    // off-screen is enough to keep it out of sight.
    frame.style.pointerEvents = 'none';

    let printed = false;
    const cleanup = () => {
        // Outlives the print dialog; removing the frame while it is open
        // cancels the job in Chrome.
        setTimeout(() => {
            if (frame.parentNode) {
                frame.parentNode.removeChild(frame);
            }
            URL.revokeObjectURL(url);
        }, 60_000);
    };

    frame.onload = () => {
        if (printed) {
            return;
        }
        printed = true;
        // note 2 — give the built-in viewer time to paint before printing.
        // Images are already decoded by the time onload fires, so they only
        // need a tick; the PDF viewer needs noticeably longer.
        setTimeout(() => {
            try {
                const win = frame.contentWindow;
                if (win) {
                    win.focus();      // note 2b
                    win.print();
                }
            } catch {
                // Renderer refused the document (corrupt file, blocked viewer).
                // Nothing actionable here; the dialog simply will not appear.
            }
            cleanup();
        }, isImage ? 100 : 700);
    };

    try {
        document.body.appendChild(frame);
        if (isImage) {
            // note 3 — scale to the printable area, one image per sheet.
            frame.srcdoc = [
                '<!doctype html><html><head><style>',
                '@page { margin: 12mm; }',
                'html,body { margin:0; padding:0; }',
                'img { max-width:100%; max-height:100vh; display:block; margin:0 auto; }',
                '</style></head><body><img src="' + url + '"></body></html>'
            ].join('');
        } else {
            frame.src = url;
        }
        return true;
    } catch {
        URL.revokeObjectURL(url);
        if (frame.parentNode) {
            frame.parentNode.removeChild(frame);
        }
        return false;
    }
}

/**
 * Build a CSV Blob for a human to open in Excel.
 *
 * The UTF-8 BOM is not a browser difference as such, but without it Excel on
 * Windows decodes exported CSVs as the local ANSI codepage and mangles every
 * non-ASCII character — which is what users report as "the export is broken
 * in <browser>" after downloading from a different machine.
 *
 * DO NOT use this for a file the application reads back in. The backend's
 * csv-parser does not strip the BOM, so the first column header arrives as
 * "﻿voucher_no" instead of "voucher_no" and the import fails to match
 * it. Use `machineCsvBlob` for anything that round-trips.
 */
export function csvBlob(csvText: string): Blob {
    return new Blob(['﻿' + csvText], { type: 'text/csv;charset=utf-8;' });
}

/**
 * Build a CSV Blob for a file the application will parse again — bulk-upload
 * templates being the case that matters. Deliberately no BOM; see `csvBlob`.
 */
export function machineCsvBlob(csvText: string): Blob {
    return new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
}

/**
 * Copy text to the clipboard, resolving to whether it worked.
 *
 * `navigator.clipboard` is undefined outside a secure context (any plain-http
 * origin that is not localhost), so calling it directly throws a TypeError
 * rather than failing gracefully. Firefox also gates `writeText` behind a
 * user gesture and rejects otherwise. The `execCommand` path is deprecated
 * but is still the only thing that works on an http:// deployment, so it
 * stays as the fallback.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // Permission denied or no user gesture — fall through.
        }
    }
    return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    // Off-screen rather than display:none — a hidden element cannot be
    // selected, and iOS Safari scrolls to a focused element that is on-screen.
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);

    try {
        textarea.select();
        // iOS Safari ignores select() on a readonly field; setSelectionRange
        // is what actually selects the text there.
        textarea.setSelectionRange(0, text.length);
        // eslint-disable-next-line deprecation/deprecation
        return document.execCommand('copy');
    } catch {
        return false;
    } finally {
        document.body.removeChild(textarea);
    }
}

/**
 * Open a tab for a URL that is only known *after* an async call.
 *
 * Safari and Firefox block `window.open` that is not on the synchronous call
 * stack of a user gesture, so opening inside an HTTP subscribe callback is
 * blocked — the user clicks and nothing happens. Calling this synchronously
 * in the click handler reserves the tab while the gesture is still live, then
 * `resolve()` points it at the real URL once the response lands.
 *
 * Returns null if the popup was blocked anyway (extension/strict settings),
 * so callers can fall back to a message instead of failing silently.
 */
export function reserveTab(): DeferredTab | null {
    const win = window.open('', '_blank');
    if (!win) {
        return null;
    }
    // Sever the opener reference — a reserved tab is same-origin until
    // navigated, so without this the destination page can reach back.
    try {
        win.opener = null;
    } catch {
        // Cross-origin already; nothing to do.
    }
    return {
        resolve: (url: string) => {
            win.location.href = url;
        },
        cancel: () => win.close()
    };
}

export interface DeferredTab {
    /** Navigate the reserved tab to the resolved URL. */
    resolve(url: string): void;
    /** Close the reserved tab — call this if the async work failed. */
    cancel(): void;
}

/**
 * Whether camera capture is actually available.
 *
 * `navigator.mediaDevices` is undefined on insecure origins, so the QR
 * scanner's `getUserMedia` call throws a TypeError there. That is
 * indistinguishable from a denied permission in a bare try/catch, and the
 * user gets told to allow camera access on a page where no prompt can ever
 * appear. Checking first lets the caller explain the real problem.
 */
export function isCameraSupported(): boolean {
    return !!navigator.mediaDevices?.getUserMedia;
}

/**
 * Human-readable reason a getUserMedia call failed, for user-facing messages.
 * The DOMException names are standardised; the wording browsers use is not.
 */
export function describeCameraError(err: unknown): string {
    if (!isCameraSupported()) {
        return window.isSecureContext
            ? 'Camera capture is not supported by this browser.'
            : 'Camera access requires a secure (HTTPS) connection.';
    }
    const name = (err as { name?: string })?.name;
    switch (name) {
        case 'NotAllowedError':
        case 'SecurityError':
            return 'Camera permission denied. Please allow camera access and try again.';
        case 'NotFoundError':
        case 'OverconstrainedError':
            return 'No camera found on this device.';
        case 'NotReadableError':
            return 'The camera is already in use by another application.';
        default:
            return 'Camera access denied or not available.';
    }
}

/**
 * Start playback of a camera preview without leaving an unhandled rejection
 * in the console.
 *
 * `HTMLMediaElement.play()` returns a Promise that rejects (AbortError /
 * NotAllowedError) whenever playback is interrupted or autoplay-blocked.
 * Ignoring the return value logs an uncaught rejection in Chrome and Safari
 * on every scanner start — noise that hides real errors.
 */
export function playPreview(video: HTMLVideoElement): void {
    const started = video.play();
    if (started && typeof started.catch === 'function') {
        started.catch(() => {
            // Autoplay blocked or the stream was torn down mid-start; the
            // caller's stop/retry path handles the user-visible side.
        });
    }
}
